import dayjs from 'dayjs';
import { Suspense } from 'react';
import { BUTTON_VARIANTS } from '@workspace/constants/common.constant';
import { stripInvisibles } from '@workspace/constants';
import { cn, getBackgroundStyle, hasTextContent, openDocsBot, shouldOpenInNewTab } from '@workspace/lib';
import { processHeading, type HeadingType } from '@workspace/lib/heading-utils';
import type { PortableTextBlock } from 'next-sanity';
import type { BynderImage } from '@workspace/interfaces';
import { Button, type ButtonVariant } from '@workspace/ui/common/Buttons/Button';
import { Picture } from '@workspace/ui/common/Image/Picture';
import { ShareIcons } from '@workspace/ui/common/Links/ShareIcons';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { Typography } from '@workspace/ui/common/Typography/Typography';
import { HeroBannerLayout, HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';
import { heroBannerVariants } from '@workspace/blocks/HeroBannerCarousel/heroBanner.variants';

interface HeroBannerContentProps {
  tag?: string;
  heading: {
    _type: string;
    fontSize?: 'h1' | 'h2' | 'h3';
    headingText: string;
  };
  description?: string;
  richDescription?: PortableTextBlock[] | null;
  contentPosition: 'left' | 'center' | 'right';
  textColor?: string;
  bynderTopImage?: BynderImage | null;
  buttons?: Array<{
    anchor?: string | null;
    isOpenInNewTab?: boolean | null;
    label: string | null;
    linkType?: 'external' | 'internal' | 'anchor' | 'askGreg';
    style?: string | null;
    url?: string | null;
  }> | null;
  socialLinks?: Array<{
    platform: string;
    color?: 'light' | 'dark';
    url?: string;
    isOpenInNewTab?: boolean;
    iconUrl?: string;
    link?: {
      url?: string;
      isOpenInNewTab?: boolean;
    };
    _ref?: string;
    _type?: 'reference';
  } | null>;
  shareIcons?: Array<{
    shareType: string;
    sharePath?: string;
    iconUrl?: string;
  } | null>;
  authorBlog?: string;
  publicDataBlog?: string;
  size?: HeroBannerSize;
  layout?: HeroBannerLayout;
}

const textAlignmentClassMap: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const buttonVariantValues: readonly ButtonVariant[] = BUTTON_VARIANTS as readonly ButtonVariant[];

const normalizeButtonVariant = (style?: string | null): ButtonVariant => {
  if (!style) return 'primary';

  const sanitized = style
    .replace(/\p{C}/gu, '')
    .replace(/button$/iu, '')
    .trim()
    .toLowerCase();

  if (buttonVariantValues.includes(sanitized as ButtonVariant)) {
    return sanitized as ButtonVariant;
  }

  return 'primary';
};

export function HeroBannerContent({
  tag,
  heading,
  description,
  richDescription,
  contentPosition,
  textColor,
  bynderTopImage,
  buttons,
  socialLinks,
  shareIcons,
  authorBlog,
  publicDataBlog,
  size = 'medium',
  layout = 'overlay',
}: HeroBannerContentProps) {
  const { text: rawHeadingText } = processHeading(heading as HeadingType);
  // Normalize non-breaking spaces to regular spaces for proper text wrapping
  const resolvedHeadingText = rawHeadingText?.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
  const minHeightClass = getHeroBannerMinHeightClass(size);

  // The `content` field (passed as `richDescription`) takes precedence over the legacy `description` string until existing descriptions are migrated
  const hasRichContent = Boolean(richDescription?.some(hasTextContent));
  const hasDescription = hasRichContent || Boolean(description);

  // Safety check: ensure we always have some content to display
  const hasContent =
    resolvedHeadingText ||
    tag ||
    hasDescription ||
    (buttons && buttons.length > 0) ||
    (socialLinks && socialLinks.length > 0);

  // Use getBackgroundStyle to get color value, then extract the backgroundColor
  const colorValue = getBackgroundStyle(textColor).backgroundColor;

  // Handle author and publication date formatting
  const publicDataBlogRender = publicDataBlog ? dayjs(publicDataBlog).format('D MMMM YYYY') : null;

  const articleDate = publicDataBlog ? dayjs(publicDataBlog) : null;
  const articleDay = articleDate?.date();
  const ordinalSuffix = articleDay
    ? articleDay % 10 === 1 && articleDay % 100 !== 11
      ? 'st'
      : articleDay % 10 === 2 && articleDay % 100 !== 12
        ? 'nd'
        : articleDay % 10 === 3 && articleDay % 100 !== 13
          ? 'rd'
          : 'th'
    : '';
  const metaArticle =
    layout === 'article' && articleDate && articleDay
      ? `Posted on ${articleDay}${ordinalSuffix} ${articleDate.format('MMMM, YYYY')}`
      : authorBlog && publicDataBlog
        ? `By ${authorBlog} ${publicDataBlogRender}`
        : authorBlog
          ? `By ${authorBlog}`
          : publicDataBlog
            ? `Published on ${publicDataBlogRender}`
            : '';

  const {
    content,
    headingDescription,
    tag: tagSlot,
    tagText,
    heading: headingSlot,
    descriptionWrapper,
    description: descriptionSlot,
    metadataWrapper,
    metadata,
    shareIcons: shareIconsSlot,
  } = heroBannerVariants({ layout, size, contentPosition });

  const handleAskGregClick = () => {
    openDocsBot();
  };

  return (
    <div
      className={cn(
        'hero-container flex h-auto flex-col transition-all duration-700',
        layout === 'article' ? 'min-h-0 justify-center' : cn('justify-center', minHeightClass),
        contentPosition === 'left' && 'items-start',
        textAlignmentClassMap[contentPosition],
        contentPosition === 'center' && 'mx-auto items-center',
        contentPosition === 'right' && 'ml-auto items-end',
        'max-md:mx-auto',
        layout === 'article' && 'w-full items-start self-stretch',
        content(),
      )}
    >
      {bynderTopImage && (
        <Picture image={bynderTopImage} desktopWidth={200} desktopHeight={200} imgClassName="mb-4 w-full max-w-40" />
      )}

      {tag && (
        <div
          className={cn('w-max', layout === 'article' ? 'flex items-center justify-center' : 'inline-block', tagSlot())}
        >
          <Typography
            variant="button"
            className={cn('block whitespace-pre-line', layout === 'overlay' && 'h-5', tagText())}
          >
            {tag}
          </Typography>
        </div>
      )}

      <div className={cn(layout === 'article' ? 'flex w-full flex-col' : 'contents', headingDescription())}>
        {(resolvedHeadingText || !hasContent) && (
          <Typography
            variant="h1"
            className={cn('wrap-break-word', headingSlot())}
            style={layout === 'overlay' ? { color: colorValue } : undefined}
          >
            {resolvedHeadingText}
          </Typography>
        )}

        {hasDescription && (
          <div
            className={cn(
              contentPosition === 'center' && 'mx-auto',
              contentPosition === 'right' && 'ml-auto',
              descriptionWrapper(),
            )}
          >
            {hasRichContent && richDescription ? (
              <CustomPortableText
                value={richDescription}
                className={descriptionSlot()}
                style={layout === 'overlay' ? { color: colorValue } : undefined}
              />
            ) : (
              <Typography
                variant="bodySm"
                className={cn('whitespace-pre-line', descriptionSlot())}
                style={layout === 'overlay' ? { color: colorValue } : undefined}
              >
                {description}
              </Typography>
            )}
          </div>
        )}
      </div>

      {metaArticle && (
        <div
          className={cn(
            contentPosition === 'center' && 'mx-auto',
            contentPosition === 'right' && 'ml-auto',
            metadataWrapper(),
          )}
        >
          <Typography
            variant="bodySm"
            className={cn('whitespace-pre-line', metadata())}
            style={layout === 'overlay' ? { color: colorValue } : undefined}
          >
            {metaArticle}
          </Typography>
        </div>
      )}

      {Array.isArray(buttons) && buttons.length > 0 && (
        <div
          className={cn(
            'flex gap-xs md:gap-xs w-full max-xs:flex-col mt-xs md:mt-0 z-50 flex-wrap',
            contentPosition === 'center' && 'justify-center',
            contentPosition === 'right' && 'justify-end',
          )}
        >
          {buttons.map((btn, index) => {
            const label = stripInvisibles(btn.label || '').trim();
            return (
              <Button
                key={index}
                variant={normalizeButtonVariant(btn.style)}
                url={
                  btn.linkType === 'askGreg'
                    ? undefined
                    : btn.linkType === 'anchor'
                      ? btn.anchor
                        ? `#${btn.anchor}`
                        : undefined
                      : btn?.url || undefined
                }
                openInNewTab={shouldOpenInNewTab({
                  type: btn.linkType,
                  isOpenInNewTab: btn.isOpenInNewTab,
                })}
                onClick={btn.linkType === 'askGreg' ? handleAskGregClick : undefined}
                className="max-h-11 w-full xs:max-w-max font-semibold! xs:min-w-5xl gap-sm min-w-0 z-50"
              >
                <span className="truncate md:min-w-max">{btn.linkType === 'askGreg' ? 'Ask Greg' : label}</span>
              </Button>
            );
          })}
        </div>
      )}

      {Array.isArray(shareIcons) && shareIcons.length > 0 && (
        <div
          className={cn(
            'flex gap-5',
            contentPosition === 'center' && 'justify-center',
            contentPosition === 'right' && 'justify-end',
          )}
        >
          <Suspense fallback={null}>
            <ShareIcons
              icons={shareIcons.filter((icon): icon is NonNullable<typeof icon> => icon !== null && !!icon?.shareType)}
              className={shareIconsSlot()}
              // socialLinksData={socialLinks}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
